import * as React from 'react';
import {twMerge} from 'tailwind-merge';

type SegmentedButtonOption<T extends string> = {
  value: T;
  label: React.ReactNode;
};

type Props<T extends string> = {
  options: ReadonlyArray<SegmentedButtonOption<T>>;
  value: T;
  onChange: (value: T) => void;
  className?: string;
};

/**
 * Segmented control: equal-width segments in a muted track, with a raised
 * thumb that slides to the selected option. Use for switching between a
 * small number of mutually exclusive modes (2-4 short labels); for longer
 * lists use a select instead.
 */
export const SegmentedButton = <T extends string>({
  options,
  value,
  onChange,
  className,
}: Props<T>) => {
  const selectedIndex = options.findIndex((option) => option.value === value);

  return (
    <div
      className={twMerge(
        'bg-muted relative inline-grid auto-cols-fr grid-flow-col rounded-lg p-1',
        className,
      )}
    >
      {selectedIndex >= 0 && (
        <div
          aria-hidden
          // Slides between segments; the track is padded by 0.25rem on each
          // side, so each segment is (100% - 0.5rem) / n wide and translateX
          // is relative to the thumb's own width
          className="bg-background absolute inset-y-1 left-1 rounded-md shadow-sm transition-transform duration-300 ease-[cubic-bezier(0.3,1.35,0.5,1)] motion-reduce:transition-none"
          style={{
            width: `calc((100% - 0.5rem) / ${options.length})`,
            transform: `translateX(${selectedIndex * 100}%)`,
          }}
        />
      )}
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          className={`relative cursor-pointer rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
            option.value === value
              ? 'text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};
